import {
  buildPriceWsMessage,
  ErrorHandler,
  getTickerKey,
  normalizePriceUpdate,
  PRICE_WS_MAX_HIDDEN_TAB_IDLE_TIME_MS,
  PRICE_WS_NORMAL_CLOSURE,
  PriceWsAction,
  PriceWsCloseReason,
  PriceWsEvent,
  SocketResponses,
  TickerPayload,
  UpdateHandler,
  uniqueBy,
} from '@helpers';
import { config } from '@terminal/config';
import { useBaseWs } from '@terminal/services/use-base-ws/useBaseWs';
import { WebSocketStatus } from '@terminal/services/use-base-ws/utils';

export type PriceWsProps = {
  updateHandler: UpdateHandler;
  delayedPrices?: boolean;
  onError?: ErrorHandler;
  getSubscribedTickers: () => TickerPayload[];
};

export interface PriceWSInstance {
  subscribe: (tickers: TickerPayload[]) => void;
  unsubscribe: (tickers: TickerPayload[]) => void;
  disconnect: (code?: number, reason?: PriceWsCloseReason) => void;
  readonly status: WebSocketStatus;
}

export const createPriceWS = ({
  updateHandler,
  delayedPrices,
  onError,
  getSubscribedTickers,
}: PriceWsProps): PriceWSInstance => {
  const {
    api: { wsRoot, wsDelayedRoot },
  } = config;
  const url = delayedPrices ? wsDelayedRoot : wsRoot;

  let closeTimer: number | undefined;

  const sendTickerMessage = (
    action: PriceWsAction,
    { ticker, trading_country, exchange }: TickerPayload
  ) => {
    base.sendMessage(
      buildPriceWsMessage({
        action,
        ticker,
        trading_country,
        exchange,
        delayedPrices,
      })
    );
  };

  const handleMessage = (payload: SocketResponses) => {
    switch (payload.event) {
      case PriceWsEvent.PRICE: {
        const update = normalizePriceUpdate(payload);

        if (update) {
          updateHandler(update);
        }
        break;
      }
      case PriceWsEvent.ERROR:
        onError?.(payload.message);
        break;
      case PriceWsEvent.SUBSCRIBED:
      case PriceWsEvent.UNSUBSCRIBED:
        break;
      default:
        onError?.(`Unknown event: ${(payload as { event: string }).event}`);
    }
  };

  const subscribeTickers = (tickers: TickerPayload[]) => {
    tickers.forEach(ticker =>
      sendTickerMessage(PriceWsAction.SUBSCRIBE, ticker)
    );
  };

  const onOpen = () => {
    const tickers = uniqueBy(getSubscribedTickers(), getTickerKey);
    subscribeTickers(tickers);
  };

  const onClose = (event: CloseEvent) => {
    clearTimeout(closeTimer);
    closeTimer = undefined;
    if (event.reason === PriceWsCloseReason.NoConnectionRequired) {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
  };

  const scheduleClose = (reason: PriceWsCloseReason) => {
    closeTimer = window.setTimeout(
      () => base.disconnect(PRICE_WS_NORMAL_CLOSURE, reason),
      PRICE_WS_MAX_HIDDEN_TAB_IDLE_TIME_MS
    ) as unknown as number;
  };

  const onVisibilityChange = () => {
    window.clearTimeout(closeTimer);

    const isAlive =
      base.status !== WebSocketStatus.CLOSED &&
      base.status !== WebSocketStatus.UNINSTANTIATED;

    if (document.hidden && isAlive) {
      scheduleClose(PriceWsCloseReason.InactiveTab);
    } else if (!isAlive) {
      base.connect();
    }
  };

  const base = useBaseWs(url, {
    onOpen: () => onOpen(),
    onClose: (event: CloseEvent) => onClose(event),
    onMessage: (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data) as SocketResponses;
        handleMessage(data);
      } catch (_error) {
        onError?.('WebSocket: Failed to parse message');
      }
    },
    onError: () => onError?.('WebSocket: Transport error'),
    shouldReconnect: () => true,
    reconnectAttempts: 5,
  });

  document.addEventListener('visibilitychange', onVisibilityChange);

  return {
    subscribe: (tickers: TickerPayload[]) => {
      window.clearTimeout(closeTimer);

      const uniqueTickers = uniqueBy(tickers, getTickerKey);
      if (base.isConnected && uniqueTickers.length > 0) {
        subscribeTickers(uniqueTickers);
      }
    },
    unsubscribe: (tickers: TickerPayload[]) => {
      if (!base.isConnected) {
        return;
      }

      const uniqueTickers = uniqueBy(tickers, getTickerKey);
      uniqueTickers.forEach(ticker =>
        sendTickerMessage(PriceWsAction.UNSUBSCRIBE, ticker)
      );

      if (getSubscribedTickers().length === 0) {
        scheduleClose(PriceWsCloseReason.NoConnectionRequired);
      }
    },
    disconnect: (code?: number, reason?: PriceWsCloseReason) => {
      base.disconnect(code, reason);
    },
    get status() {
      return base.status;
    },
  };
};
